// primitive buttons are used here for native level screen reader support
/* eslint-disable @atlaskit/design-system/no-html-button */
import React, { type KeyboardEvent } from 'react';
import { Text } from '@atlaskit/primitives/compiled';
import { useIntl } from '@atlassian/jira-intl';
import type { Positionable } from '../../../common/types/index.tsx';
import { DIRECTION_KEYS, GLOBAL_LOOPS_CONTAINER_TYPE } from '../../../common/constants/index.tsx';
import messages from '../messages.tsx';
import type { Point } from '../../../common/types/diagram.tsx';

export const STANDARD_MOVE_DISTANCE = 5;
export const GREATER_MOVE_DISTANCE = 10;
export const FINE_MOVE_DISTANCE = 1;

export type Props = {
	position: Point | undefined;
	isImmutable?: boolean;
	describedBy?: string;
	isMoving: boolean;
	onFocus: () => void;
	onMoveStart: (id: Positionable) => void;
	onMoveUpdate: (id: Positionable) => void;
	onMoveComplete: () => void;
	onMoveCancel: () => void;
};

const ListContainer = ({
	position,
	isImmutable = false,
	describedBy,
	isMoving,
	onFocus,
	onMoveStart,
	onMoveUpdate,
	onMoveComplete,
	onMoveCancel,
}: Props) => {
	const { formatMessage } = useIntl();

	const handleKeyDown = (e: KeyboardEvent) => {
		const direction = DIRECTION_KEYS[e.key];

		if (isMoving && direction && position) {
			const updatedPosition = { ...position };

			let moveDistance = STANDARD_MOVE_DISTANCE;

			if (e.shiftKey) {
				moveDistance = GREATER_MOVE_DISTANCE;
			} else if (e.altKey) {
				moveDistance = FINE_MOVE_DISTANCE;
			}

			if (direction === 'up') {
				updatedPosition.y -= moveDistance;
			}
			if (direction === 'down') {
				updatedPosition.y += moveDistance;
			}
			if (direction === 'left') {
				updatedPosition.x -= moveDistance;
			}
			if (direction === 'right') {
				updatedPosition.x += moveDistance;
			}

			onMoveUpdate({
				id: GLOBAL_LOOPS_CONTAINER_TYPE,
				type: GLOBAL_LOOPS_CONTAINER_TYPE,
				position: updatedPosition,
			});
		}
	};

	const handleKeyUp = (e: KeyboardEvent) => {
		if (e.key === 'm' && !isMoving && position) {
			onMoveStart({
				id: GLOBAL_LOOPS_CONTAINER_TYPE,
				type: GLOBAL_LOOPS_CONTAINER_TYPE,
				position,
			});
		}

		if ((e.key === 'Enter' || e.key === ' ') && isMoving) {
			onMoveComplete();
		}

		if (e.key === 'Escape' && isMoving) {
			onMoveCancel();
		}
	};

	if (isImmutable) {
		return <Text>{formatMessage(messages.containerLabel)}</Text>;
	}

	return (
		<button
			type="button"
			onFocus={onFocus}
			onKeyDown={handleKeyDown}
			onKeyUp={handleKeyUp}
			aria-describedby={describedBy}
		>
			<Text>{formatMessage(messages.containerLabel)}</Text>
		</button>
	);
};

export default ListContainer;
